import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { DviSignalData } from '@/hooks/useDviSignals';

interface DviQualityScoreCardProps {
  signals: DviSignalData | null | undefined;
  isLoading: boolean;
}

export function DviQualityScoreCard({ signals, isLoading }: DviQualityScoreCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Quality Score</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-8 w-20 mb-2" />
          <Skeleton className="h-2 w-full" />
        </CardContent>
      </Card>
    );
  }

  const score = signals?.quality_score ?? 0;
  // Note: trend_direction may not be available in all signal responses
  const trend = (signals as any)?.trend_direction as string | undefined;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getProgressColor = (score: number) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const TrendIcon = trend === 'improving' 
    ? TrendingUp 
    : trend === 'declining' 
      ? TrendingDown 
      : Minus;

  const trendColor = trend === 'improving' 
    ? 'text-green-600' 
    : trend === 'declining' 
      ? 'text-red-600' 
      : 'text-muted-foreground';

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center justify-between">
          Quality Score
          <TrendIcon className={`h-4 w-4 ${trendColor}`} />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-2xl font-bold ${getScoreColor(score)}`}>
          {score}/100
        </div>
        <Progress 
          value={score} 
          className="mt-2 h-2"
          // The indicator color is controlled by CSS
        />
        <p className="text-xs text-muted-foreground mt-2">
          {score >= 80 && 'Excellent calibration'}
          {score >= 60 && score < 80 && 'Good calibration'}
          {score >= 40 && score < 60 && 'Needs improvement'}
          {score < 40 && 'Review recommendations'}
        </p>
      </CardContent>
    </Card>
  );
}
